import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import Pagination from "../../../components/BaseUI/Pagination";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import {
  buildDateRangeSection,
  buildRadioFilterSection,
  countTruthy,
} from "../shared/buildListFilterSections";
import {
  FiFileText,
  FiRefreshCw,
  FiClock,
  FiUser,
  FiAlertCircle,
  FiEye,
} from "react-icons/fi";
import "./Logs.scss";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const EMPTY_LOG_FILTERS = {
  method: "",
  statusCode: "",
  startDate: "",
  endDate: "",
};

const Logs = () => {
  useTitle("Rovion MES - İşlem Geçmişi");
  const { getToken } = useAuth();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [stats, setStats] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState(EMPTY_LOG_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_LOG_FILTERS);

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(filters.method && { method: filters.method }),
        ...(filters.statusCode && { statusCode: filters.statusCode }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
      };

      const response = await axios.get(`${API_URL}/api/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data?.status && response.data?.data) {
        setLogs(response.data.data.logs || []);
        const paginationData = response.data.data.pagination || {};
        setPagination((prev) => ({
          ...prev,
          page: paginationData.page || 1,
          limit: paginationData.limit || 50,
          total: paginationData.total || 0,
          totalPages: paginationData.pages || 0,
          hasNextPage: (paginationData.page || 1) < (paginationData.pages || 0),
          hasPrevPage: (paginationData.page || 1) > 1,
        }));
        setStats(response.data.data.stats || null);
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
      addToast("İşlem geçmişi yüklenemedi", "danger");
      setLogs([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchLogs();
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusVariant = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "success";
    if (statusCode >= 400) return "danger";
    return "default";
  };

  const getStatusLabel = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "Başarılı";
    if (statusCode >= 400 && statusCode < 500) return "Hata";
    if (statusCode >= 500) return "Sunucu Hatası";
    return "Bilinmeyen";
  };

  const getMethodLabel = (method) => {
    const labels = {
      POST: "Oluşturma",
      PUT: "Güncelleme",
      PATCH: "Güncelleme",
      DELETE: "Silme",
    };
    return labels[method] || method;
  };

  const headers = [
    {
      name: "İşlem",
      dataType: "text",
      id: 0,
      customSorter: (a, b) => {
        const la = String(a.readableAction || getMethodLabel(a.method) || "");
        const lb = String(b.readableAction || getMethodLabel(b.method) || "");
        return la.localeCompare(lb, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      },
    },
    {
      name: "Kullanıcı",
      dataType: "text",
      id: 1,
      customSorter: (a, b) => {
        const ua = String(a.userEmail || a.userId?.email || "");
        const ub = String(b.userEmail || b.userId?.email || "");
        return ua.localeCompare(ub, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      },
    },
    {
      name: "Tarih & Saat",
      dataType: "date",
      id: 2,
      customSorter: (a, b) => {
        const ta = new Date(a?.timestamp).getTime();
        const tb = new Date(b?.timestamp).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    {
      name: "Durum",
      dataType: "number",
      id: 3,
      customSorter: (a, b) =>
        (Number(a.statusCode) || 0) - (Number(b.statusCode) || 0),
    },
    { name: "İşlemler", id: 4, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("logs-list", headers);

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, pagination.limit, filters]);

  const handleViewLog = (log) => {
    setSelectedLog(log);
  };

  const handleClearFilters = useCallback(() => {
    setDraftFilters(EMPTY_LOG_FILTERS);
    setFilters(EMPTY_LOG_FILTERS);
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const activeFilterCount = useMemo(
    () =>
      countTruthy([
        filters.method,
        filters.statusCode,
        filters.startDate,
        filters.endDate,
      ]),
    [filters],
  );

  const logFilterSections = useMemo(
    () => [
      buildRadioFilterSection({
        id: "method",
        title: "İşlem Tipi",
        value: draftFilters.method,
        onChange: (v) =>
          setDraftFilters((prev) => ({ ...prev, method: String(v ?? "") })),
        options: [
          { value: "", label: "Tümü" },
          { value: "POST", label: "Oluşturma" },
          { value: "PUT", label: "Güncelleme" },
          { value: "DELETE", label: "Silme" },
          { value: "PATCH", label: "Güncelleme (PATCH)" },
        ],
      }),
      buildRadioFilterSection({
        id: "statusCode",
        title: "Durum",
        value: draftFilters.statusCode,
        onChange: (v) =>
          setDraftFilters((prev) => ({
            ...prev,
            statusCode: String(v ?? ""),
          })),
        options: [
          { value: "", label: "Tümü" },
          { value: "200", label: "Başarılı (200)" },
          { value: "400", label: "Hata (400)" },
          { value: "500", label: "Sunucu Hatası (500)" },
        ],
      }),
      buildDateRangeSection({
        startDate: draftFilters.startDate,
        endDate: draftFilters.endDate,
        onStartChange: (v) =>
          setDraftFilters((prev) => ({ ...prev, startDate: v })),
        onEndChange: (v) =>
          setDraftFilters((prev) => ({ ...prev, endDate: v })),
      }),
    ],
    [draftFilters],
  );

  const handleApplyFilters = useCallback(() => {
    setFilters(draftFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [draftFilters]);

  const filteredLogs = useMemo(() => {
    if (!searchQuery) return logs;
    const query = searchQuery.toLowerCase();
    return logs.filter((log) => {
      return (
        (log.readableAction || "").toLowerCase().includes(query) ||
        (log.userEmail || "").toLowerCase().includes(query) ||
        (log.path || "").toLowerCase().includes(query)
      );
    });
  }, [logs, searchQuery]);

  return (
    <AppPage
      title="Uygulama Logları"
      subtitle={`${stats?.totalRequests || 0} Toplam İşlem`}
      icon={<FiFileText />}
      actions={[
        <Button
          key="refresh"
          appearance="primary-subtle"
          onClick={onRefresh}
          disabled={refreshing}
          icon={<FiRefreshCw className={refreshing ? "spin" : ""} />}
        >
          Yenile
        </Button>,
      ]}
    >
      <CardContainer>
        {loading && !refreshing ? (
          <div className="loading-container">
            <FiFileText className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="logs-list"
              checkboxSelection={false}
              loading={loading}
              search={false}
              hideFooter={true}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  filterSections={logFilterSections}
                  activeFilterCount={activeFilterCount}
                  onApply={handleApplyFilters}
                  onReset={handleClearFilters}
                />
              }
            >
              {Array.isArray(filteredLogs) &&
                filteredLogs.map((log) => (
                  <Tr
                    key={log._id}
                    id={log._id}
                    data={log}
                    className={log.statusCode >= 400 ? "error-row" : ""}
                  >
                    {visibleColumns.includes(0) && (
                      <Td>
                        <div className="operation-cell">
                          <strong>
                            {log.readableAction || getMethodLabel(log.method)}
                          </strong>
                          {log.error && (
                            <div className="error-badge">
                              <FiAlertCircle size={12} />
                              <span>{log.error}</span>
                            </div>
                          )}
                        </div>
                      </Td>
                    )}
                    {visibleColumns.includes(1) && (
                      <Td>
                        <div className="user-cell">
                          <FiUser size={14} />
                          {log.userEmail ||
                            log.userId?.email ||
                            "Bilinmeyen Kullanıcı"}
                        </div>
                      </Td>
                    )}
                    {visibleColumns.includes(2) && (
                      <Td>
                        <div className="time-cell">
                          <FiClock size={14} />
                          {formatDateTime(log.timestamp)}
                        </div>
                      </Td>
                    )}
                    {visibleColumns.includes(3) && (
                      <Td>
                        <Lozenge appearance={getStatusVariant(log.statusCode)}>
                          {getStatusLabel(log.statusCode)}
                        </Lozenge>
                      </Td>
                    )}
                    {visibleColumns.includes(4) && (
                      <Td>
                        <div className="d-flex align-items-center gap-1">
                          <Button
                            appearance="subtle"
                            icon={<FiEye style={{ fontSize: "18px" }} />}
                            onClick={() => handleViewLog(log)}
                            aria-label="Detay"
                          />
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
            </Table>

            {(!filteredLogs || filteredLogs.length === 0) && (
              <div className="empty-state">
                <FiFileText size={64} />
                <p>İşlem geçmişi bulunamadı</p>
              </div>
            )}

            {/* Pagination */}
            {!loading && pagination.total > 0 && (
              <div className="mt-3">
                <Pagination
                  totalItems={pagination.total}
                  totalPerPage={pagination.limit}
                  currentPage={pagination.page}
                  totalRecords={pagination.total}
                  onPageChange={({ pagination: paginationData }) => {
                    setPagination((prev) => ({
                      ...prev,
                      page: paginationData.currentPage,
                      limit: paginationData.itemsPerPage,
                    }));
                  }}
                  options={{
                    skipPrevious: true,
                    previous: true,
                    next: true,
                    skipNext: true,
                    selectQuantity: true,
                    simpleCounting: false,
                  }}
                />
              </div>
            )}
          </>
        )}
      </CardContainer>

      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        width="large"
      >
        <ModalHeader>İşlem Detayları</ModalHeader>
        <ModalBody>
          {selectedLog && (
            <>
              <div className="detail-section">
                <h4>Genel Bilgiler</h4>
                <div className="detail-grid">
                  <div>
                    <strong>İşlem:</strong>{" "}
                    {selectedLog.readableAction ||
                      getMethodLabel(selectedLog.method)}
                  </div>
                  <div>
                    <strong>Durum:</strong>{" "}
                    <Lozenge
                      appearance={getStatusVariant(selectedLog.statusCode)}
                    >
                      {getStatusLabel(selectedLog.statusCode)}
                      {/* ({selectedLog.statusCode}) */}
                    </Lozenge>
                  </div>
                  <div>
                    <strong>Kullanıcı:</strong>{" "}
                    {selectedLog.userEmail ||
                      selectedLog.userId?.email ||
                      "Bilinmeyen"}
                  </div>
                  <div>
                    <strong>Tarih ve Saat:</strong>{" "}
                    {formatDateTime(selectedLog.timestamp)}
                  </div>
                </div>
              </div>

              {selectedLog.error && (
                <div className="detail-section">
                  <h4>Hata Mesajı</h4>
                  <div className="error-message">{selectedLog.error}</div>
                </div>
              )}

              {selectedLog.requestBody &&
                Object.keys(selectedLog.requestBody).length > 0 && (
                  <div className="detail-section">
                    <h4>Gönderilen Veriler</h4>
                    <pre>
                      {JSON.stringify(selectedLog.requestBody, null, 2)}
                    </pre>
                  </div>
                )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="danger-subtle"
            onClick={() => setSelectedLog(null)}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer>
        {Array.isArray(toasts) &&
          toasts.map((toast) => (
            <Toast
              key={toast.id}
              title={toast.message}
              appearance={toast.type}
              onClose={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
            />
          ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(Logs, ["admin", "employee", "user"]);
